// Creates an Ask Heidi document, then writes its content. Content type is
// resolved by the caller (flag + provider) and passed in.
import {
  ContentType,
  DocumentGenType,
  DocumentTabType,
  GenerationMethod,
  type Brain,
} from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  GET_DOCUMENT_BY_ID,
  GET_DOCUMENTS_BY_ID,
  mockEmptyDocument,
  StreamingStatus,
  TabType,
  type GetDocumentsResponse,
} from '../../contracts';
import { postCreateEmptyDocument, updateDocument } from '../api/documents';
import { mapNewBrain } from '../brainUtils';
import {
  useSessionWindow,
  useSessionWindows,
} from '../session-windows/useQuerySessionWindows';

type CreateFromAskAiInput = {
  content: string;
  aiCommandText: string;
  brain?: Brain;
  name?: string;
  contentType?: ContentType;
};

export function useMutateCreateDocumentFromAskAI(
  sessionId: string,
  {
    resolveContentType,
    enableBlockEditorV1,
    isFreeUser,
    onCreateError,
  }: {
    resolveContentType: (sessionId: string) => ContentType;
    enableBlockEditorV1: boolean;
    isFreeUser: boolean;
    onCreateError?: (error: Error) => void;
  }
) {
  const queryClient = useQueryClient();
  const { activeWindowIndex } = useSessionWindows({ sessionId });
  const { addTabAndSetActive } = useSessionWindow({
    sessionId,
    windowIndex: activeWindowIndex,
  });

  return useMutation({
    mutationKey: ['createDocument', { sessionId }],
    mutationFn: async (input: CreateFromAskAiInput) => {
      const documents = queryClient.getQueryData<GetDocumentsResponse>([
        GET_DOCUMENTS_BY_ID,
        { sessionId: sessionId },
      ]);
      const documentResponse = await postCreateEmptyDocument({
        contentType: resolveContentType(sessionId),
        sessionId,
        documentTabType: DocumentTabType.DOCUMENT,
        generationMethod: GenerationMethod.ASK_AI,
        aiCommandText: input.aiCommandText,
        brain: input.brain,
        index: documents?.total,
      });

      documentResponse.data.brain = mapNewBrain(documentResponse.data.brain, isFreeUser);
      const document = documentResponse.data;

      const data = await updateDocument({
        sessionId,
        documentId: document.id,
        name: input.name || document?.name || '',
        content: input.content,
        generationType: DocumentGenType.ASK_AI,
        updatedDictation: document?.updated_dictation || '',
        contentType:
          input.contentType ||
          (enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN),
      });

      return { documentResponse, data };
    },
    onSuccess: ({ documentResponse }, { content }) => {
      queryClient.setQueryData<GetDocumentsResponse>(
        [GET_DOCUMENTS_BY_ID, { sessionId: sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          const docs = oldData.documents || [];

          if (docs) {
            docs.push({
              ...documentResponse.data,
              id: documentResponse.data.id,
              generation_type: documentResponse.data.generation_type,
              created_at: new Date().toISOString(),
              session_id: sessionId,
              content,
            });
          }

          const newData: GetDocumentsResponse = {
            ...oldData,
          };
          return newData;
        }
      );
    },
    onMutate: (input) => {
      const documents = queryClient.getQueryData<GetDocumentsResponse>([
        GET_DOCUMENTS_BY_ID,
        { sessionId: sessionId },
      ]);
      queryClient.setQueryData(
        [GET_DOCUMENT_BY_ID, { sessionId: sessionId, documentId: mockEmptyDocument.id }],
        () => {
          return {
            ...mockEmptyDocument,
            streamingStatus: StreamingStatus.NOT_STARTED,
          };
        }
      );
      const newData: GetDocumentsResponse = {
        documents: [
          ...(documents?.documents || []),
          {
            ...mockEmptyDocument,
            session_id: sessionId,
            name: input.name || input.aiCommandText || 'Untitled Document',
            generation_type: DocumentGenType.ASK_AI,
          },
        ],
        total: (documents?.total || 0) + 1,
      };
      queryClient.setQueryData<GetDocumentsResponse>(
        [GET_DOCUMENTS_BY_ID, { sessionId: sessionId }],
        newData
      );
    },
    onSettled: (data) => {
      if (data?.documentResponse) {
        addTabAndSetActive({
          name: TabType.DOCUMENT,
          documentId: data.documentResponse.data.id,
        });
      }

      void queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENTS_BY_ID, { sessionId: sessionId }],
      });
    },
    onError: (error) => {
      onCreateError?.(error);
    },
  });
}
